import microphonePermissionStates from '../../consts/aiAssistant/microphonePermissionStates';
import consentFlags from '../../consts/consents/consentFlags';
import {strCode} from '../../consts/strCode';
import {selectIsAiAssistantConsentAccepted} from '../../state/selectors/aiAssistant';
import {
    showAiAssistantConsentModal,
    showAiAssistantLoginModal,
    showAiAssistantMicrophoneDeniedModal,
    showAiAssistantMicrophonePermissionModal,
    showPreferenceCenterModal,
} from '../../state/slice/modal';
import {dispatch, getState} from '../../state/store';
import log from '../logger/log';
import modalService from '../modal/modalService';
import notificationService from '../notificationService';
import voxlinkSessionService from '../voxlink/voxlinkSessionService';
import aiAssistantSessionService from './aiAssistantSessionService';
import microphonePermissionService from './microphonePermissionService';

// No gateway endpoint (every env but dev) - no login step
const getIsLoggedIn = () => !voxlinkSessionService.getIsEnabled() || voxlinkSessionService.getIsLoggedIn();

// Consent → assistant login → microphone; shows the modal for the first unmet step
const checkAiAssistantPreconditions = (permissionState) => {
    const isConsentAccepted = selectIsAiAssistantConsentAccepted(getState());

    if (!isConsentAccepted) {
        log.info('aiAssistantAccessService: voice agent consent is not accepted');
        dispatch(showAiAssistantConsentModal());

        return false;
    }

    const isLoggedIn = getIsLoggedIn();

    if (!isLoggedIn) {
        log.info('aiAssistantAccessService: user is not logged in to the assistant');
        dispatch(showAiAssistantLoginModal());

        return false;
    }

    log.info(`aiAssistantAccessService: microphone permission is '${permissionState}'`);

    if (permissionState === microphonePermissionStates.DENIED) {
        dispatch(showAiAssistantMicrophoneDeniedModal());

        return false;
    }

    if (permissionState !== microphonePermissionStates.GRANTED) {
        dispatch(showAiAssistantMicrophonePermissionModal());

        return false;
    }

    return true;
};

// `microphonePermission`: the native prompt's answer - some browsers would ask again if re-queried
// `replacedModalType`: hidden together with showing the next screen, so nothing flashes
const openAiAssistant = async ({microphonePermission, replacedModalType} = {}) => {
    const permissionState = microphonePermission || (await microphonePermissionService.getPermissionState());

    if (replacedModalType) modalService.hideModalByType(replacedModalType);

    const isAccessGranted = checkAiAssistantPreconditions(permissionState);

    if (!isAccessGranted) return;

    aiAssistantSessionService.startSession();
};

const loginToAiAssistant = async () => {
    try {
        await voxlinkSessionService.createSession();

        return true;
    } catch (e) {
        log.error(
            `aiAssistantAccessService: login failed, status: ${e?.response?.status}, code: ${e?.response?.data?.code}`
        );
        notificationService.showToastNotification({messageStr: strCode.REQUEST_ERROR});

        return false;
    }
};

const requestMicrophonePermission = () => microphonePermissionService.requestPermission();

const openPreferenceCenter = () =>
    dispatch(showPreferenceCenterModal({highlightedConsentFlag: consentFlags.AI_ASSISTANT}));

export default {openAiAssistant, loginToAiAssistant, requestMicrophonePermission, openPreferenceCenter};
